import React, { useState } from "react";

/**
 * Form primitives for the sign-in page's "Previous login" (OMCi v3 tokens):
 * 40px fields, r8, Inter 14/20, focus ring on the brand border token. Nothing
 * here intercepts autofill or pasting, so password managers keep working.
 */

export const INPUT_CLASS =
    "block h-10 w-full rounded-omc border border-line bg-surface px-3 text-sm leading-5 text-ink placeholder:text-ink-muted focus:border-line-focus focus:outline-none focus:ring-2 focus:ring-line-focus disabled:bg-subtle disabled:text-ink-disabled";
const INPUT_INVALID_CLASS = "border-status-danger-border";

/** Inline text action (Forgot password?, Back to sign in) in the link colour, keyboard-focusable. */
export const TEXT_LINK_CLASS =
    "inline-block rounded-omc-sm text-sm font-medium text-ink-link hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-line-focus";

export function FieldLabel({ htmlFor, children }) {
    return (
        <label htmlFor={htmlFor} className="block text-sm font-medium leading-5 text-ink">
            {children}
        </label>
    );
}

/** A message under a field. `danger` is announced by screen readers (role=alert) without moving focus. */
export function FieldMessage({ tone = "danger", children }) {
    const danger = tone === "danger";
    return (
        <p role={danger ? "alert" : undefined} className={`mt-2 text-sm leading-5 ${danger ? "text-status-danger-fg" : "text-ink-secondary"}`}>
            {children}
        </p>
    );
}

export function TextField({ id, invalid = false, className = "", ...inputProps }) {
    return <input id={id} aria-invalid={invalid || undefined} className={`${INPUT_CLASS} ${invalid ? INPUT_INVALID_CLASS : ""} ${className}`} {...inputProps} />;
}

/**
 * Password input with the design's "Show"/"Hide" control inside the field. The
 * control is a real button (keyboard reachable) whose visible label states the
 * action; it toggles the input type and nothing else.
 */
export function PasswordField({ id, invalid = false, className = "", ...inputProps }) {
    const [visible, setVisible] = useState(false);
    return (
        <div className="relative">
            <input
                id={id}
                type={visible ? "text" : "password"}
                aria-invalid={invalid || undefined}
                className={`${INPUT_CLASS} pr-16 ${invalid ? INPUT_INVALID_CLASS : ""} ${className}`}
                {...inputProps}
            />
            <button
                type="button"
                onClick={() => setVisible((v) => !v)}
                aria-controls={id}
                className="absolute inset-y-1 right-1 rounded-omc-sm px-2 text-sm font-medium text-ink-link hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-line-focus"
            >
                {visible ? "Hide" : "Show"}
                <span className="sr-only"> password</span>
            </button>
        </div>
    );
}
